import React from 'react';

import { Button, Group, Modal, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconTags } from '@tabler/icons-react';
import useCreateAppointmentCoachRole from 'hooks/appointmentCoachRole/useCreateAppointmentCoachRole';
import { AppointmentCoachRoleWrite } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface IModalCreateAppointmentCoachRoleProps {
  opened: boolean;
  handleClose: () => void;
}

interface FormValues {
  name: string;
}

const ModalCreateAppointmentCoachRole = ({
  opened,
  handleClose,
}: IModalCreateAppointmentCoachRoleProps): JSX.Element => {
  const createMutation = useCreateAppointmentCoachRole();

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
    },

    validate: {
      name: value =>
        value.length < 3 || value.length > 120 ? 'Name must be between 3-120 characters' : null,
    },
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const { successNotification, errorNotification } = notificationHandler();
  const handleSubmit = (values: FormValues): void => {
    const payload: AppointmentCoachRoleWrite = {
      appointmentCoachRoleId: uuidv4(),
      ...values,
    };
    createMutation.mutate(payload, {
      onSuccess: () => {
        successNotification(
          'Creating coach role for appointment success',
          `${values.name} role has been successfully created.`,
        );
        handleCloseAndReset();
      },
      onError: error => {
        errorNotification('Creating coach role for appointment failed', error.response?.data.message);
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>Create Coach Role For Appointments</Title>}
      size='auto'
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            data-cy='appointmentCoachRoleTextInput'
            required
            label='Name'
            {...form.getInputProps('name')}
            icon={<IconTags />}
            placeholder='Set Role Name'
          />
          <Group position='right'>
            <Button variant='subtle' onClick={handleCloseAndReset} disabled={createMutation.isLoading}>
              Cancel
            </Button>
            <Button
              disabled={createMutation.isLoading}
              type='submit'
              data-cy='saveAppointmentCoachRoleButton'
            >
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalCreateAppointmentCoachRole;
